import React from 'react';

import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { zodResolver } from '@hookform/resolvers/zod';
import { SubmitHandler, useForm } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';

import SquareIcon from 'src/registry/components/blocks/SquareIcon';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { Button } from 'src/registry/ui/Button';
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
    DialogTrigger,
} from 'src/registry/ui/Dialog';
import { generalMessages } from 'src/registry/utils/messages';
import { useOnError } from 'src/utils/errors';
import { ICON_MAP } from 'src/utils/constants';
import { GeneralASError } from 'src/registry/types/axios';
import { cn } from 'src/utils';
import DiscountPolicyForm from 'src/features/discountPolicies/components/forms/DiscountPolicyForm';
import { useGetDiscountPolicy } from 'src/features/discountPolicies/api/useGetDiscountPolicy';
import { DiscountPolicy } from 'src/features/discountPolicies/utils/types';
import {
    discountPolicyFormSchema,
    DiscountPolicyFormSchemaValues,
} from 'src/features/discountPolicies/utils/formSchema';
import { getDirtyValues } from 'src/utils/getDirtyValues';

interface DiscountPolicyFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedPolicy?: DiscountPolicy;
    children?: React.ReactNode;
    isOpen?: boolean;
    isPending: boolean;
    icon: IconProp;
    title: string;
    subtitle: string;
    mutate: (
        data: DiscountPolicyFormSchemaValues,
        onSuccess: () => void,
        onError: (error: GeneralASError) => void,
    ) => void;
}

function DiscountPolicyFormModal<TData>({
    selectedPolicy,
    isPending,
    children,
    className,
    icon,
    title,
    subtitle,
    mutate,
}: DiscountPolicyFormModalProps<TData>) {
    const [isModalOpen, setIsModalOpen] = React.useState(false);

    const { data: discountPolicy } = useGetDiscountPolicy({
        policyId: selectedPolicy?.id ?? '',
        config: { enabled: !!selectedPolicy?.id && isModalOpen },
    });

    const onError = useOnError();

    const formatValidDuration = (value: string) => {
        const [days] = value.split(' ');
        return days;
    };

    const defaultValues = React.useMemo(() => {
        return {
            name: discountPolicy?.name ?? undefined,
            valid_from: discountPolicy?.valid_from ?? undefined,
            valid_to: discountPolicy?.valid_to ?? undefined,
            description: discountPolicy?.description ?? undefined,
            amount: discountPolicy?.amount ?? undefined,
            percent: discountPolicy?.percent ? (Number(discountPolicy?.percent) * 100).toFixed(2) : undefined,
            valid_duration: discountPolicy?.valid_duration
                ? formatValidDuration(discountPolicy?.valid_duration)
                : undefined,
            manual_valid_duration: discountPolicy?.manual_valid_duration ?? undefined,
            max_applications: discountPolicy?.max_applications ?? undefined,
            max_applications_per_customer: discountPolicy?.max_applications_per_customer ?? undefined,
            service_plans: discountPolicy?.service_plans?.map((plan) => plan.id) ?? undefined,
            service_plan_items: discountPolicy?.service_plan_items?.map((item) => item.id) ?? undefined,
            currency: discountPolicy?.currency ?? undefined,
        };
    }, [discountPolicy]);

    const form = useForm<DiscountPolicyFormSchemaValues>({
        resolver: zodResolver(discountPolicyFormSchema),
        defaultValues,
    });

    React.useEffect(() => {
        form.reset(defaultValues);
    }, [form, isModalOpen, defaultValues]);

    const onOpenChange = (isOpen: boolean) => {
        if (!isOpen) form.reset();
        setIsModalOpen(isOpen);
    };

    const onSubmit: SubmitHandler<DiscountPolicyFormSchemaValues> = React.useCallback(
        (values: DiscountPolicyFormSchemaValues) => {
            const toDecimalPercentString = (value?: string | null) => {
                if (!value) return;
                return (Number(value) / 100).toFixed(4).toString();
            };

            const getTransformedValues = (values: DiscountPolicyFormSchemaValues) => {
                return {
                    ...values,
                    percent: toDecimalPercentString(values.percent),
                    valid_duration: values.valid_duration != null ? `P${values.valid_duration}D` : null,
                };
            };

            const transformedValues = getTransformedValues(values);
            const dirtyFields = getDirtyValues(form.formState.dirtyFields, transformedValues);

            const payload: DiscountPolicyFormSchemaValues = {
                ...dirtyFields,
                name: transformedValues.name,
                valid_from: transformedValues.valid_from,
                manual_valid_duration: transformedValues.manual_valid_duration,
                currency: transformedValues.currency,
            };

            mutate(
                payload,
                () => setIsModalOpen(false),
                (error) => onError(error, form.setError),
            );
        },
        [form.formState.dirtyFields, form.setError, mutate, onError],
    );

    return (
        <Dialog modal open={isModalOpen} onOpenChange={onOpenChange}>
            {children ? (
                <DialogTrigger asChild>{children}</DialogTrigger>
            ) : (
                <DialogTrigger asChild>
                    <Button className={cn('ml-auto', className)}>
                        <FontAwesomeIcon icon={ICON_MAP.ADD} />
                        {title}
                    </Button>
                </DialogTrigger>
            )}
            <DialogContent>
                <DialogHeader>
                    <div className="flex flex-row gap-2">
                        <SquareIcon icon={icon} />
                        <div>
                            <DialogTitle>{title}</DialogTitle>
                            <DialogDescription>{subtitle}</DialogDescription>
                        </div>
                    </div>
                </DialogHeader>
                <DialogSeparator />
                <DialogBody>
                    <DiscountPolicyForm form={form} onSubmit={onSubmit} selectedDiscountPolicy={selectedPolicy} />
                </DialogBody>
                <DialogSeparator />
                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline" type="button" className="flex-1">
                            <FormattedMessage {...generalMessages.cancel} />
                        </Button>
                    </DialogClose>
                    <Button
                        variant="default"
                        disabled={isPending || (selectedPolicy && !form.formState.isDirty)}
                        type="submit"
                        onClick={form.handleSubmit(onSubmit)}
                        className="flex-1"
                    >
                        {isPending && <FontAwesomeIcon icon={ICON_MAP.SPINNER} className="mr-2 size-4 animate-spin" />}
                        {title}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default DiscountPolicyFormModal;
